import type { CrmApi } from './contract';

/**
 * The single door to data. Views import `api` from here and nothing else; the
 * implementation behind it is swappable, which is the whole point of the
 * `CrmApi` interface.
 *
 * Nothing here imports the mock. That is deliberate: a static import would pull
 * the seed generator and its catalogues into the entry chunk, where they would
 * cost every user a download they may never need. `installApi()` in
 * `./bootstrap` pulls in whatever this environment needs dynamically at
 * startup instead, without touching a single view.
 */
let instance: CrmApi | undefined;

const client = (): CrmApi => {
  if (!instance) {
    throw new Error(
      'No hay cliente de API instalado. Llama a installApi() (o setApiClient) antes de renderizar.',
    );
  }
  return instance;
};

export const setApiClient = (next: CrmApi): void => {
  instance = next;
};

export const getApiClient = (): CrmApi => client();

/** A stable façade: swapping the client never invalidates a captured reference. */
export const api: CrmApi = {
  get session() {
    return client().session;
  },
  get tenants() {
    return client().tenants;
  },
  get users() {
    return client().users;
  },
  get teams() {
    return client().teams;
  },
  get tags() {
    return client().tags;
  },
  get agentes() {
    return client().agentes;
  },
  get corridas() {
    return client().corridas;
  },
  get tickets() {
    return client().tickets;
  },
  get escalamientos() {
    return client().escalamientos;
  },
  get integraciones() {
    return client().integraciones;
  },
  get apiKeys() {
    return client().apiKeys;
  },
  get bases() {
    return client().bases;
  },
  get correos() {
    return client().correos;
  },
  get reuniones() {
    return client().reuniones;
  },
  get whatsapp() {
    return client().whatsapp;
  },
  get drive() {
    return client().drive;
  },
  get hubspot() {
    return client().hubspot;
  },
  get softphone() {
    return client().softphone;
  },
  get pipelines() {
    return client().pipelines;
  },
  get companies() {
    return client().companies;
  },
  get contacts() {
    return client().contacts;
  },
  get leads() {
    return client().leads;
  },
  get deals() {
    return client().deals;
  },
  get activities() {
    return client().activities;
  },
  get tasks() {
    return client().tasks;
  },
  get conversations() {
    return client().conversations;
  },
  get calls() {
    return client().calls;
  },
  get customFields() {
    return client().customFields;
  },
  get savedViews() {
    return client().savedViews;
  },
  get auditEvents() {
    return client().auditEvents;
  },
  get notifications() {
    return client().notifications;
  },
  get analytics() {
    return client().analytics;
  },
};

export * from './contract';
export * from './analytics-contract';
export * from './errors';
export * from './query-keys';
export * from './bootstrap';
export * from './unauthorized';
